import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * Unified searchable dropdown — supports single and multi modes.
 *
 * Props:
 *  - multi?: boolean                       (default false)
 *  - value?: string                        (single mode)
 *  - values?: string[]                     (multi mode)
 *  - onChange: (next: string | string[]) => void
 *  - options: Array<{ value: string; label: string }>
 *  - placeholder?: string
 *  - searchPlaceholder?: string
 *  - testId?: string
 *  - disabled?: boolean
 *  - clearable?: boolean                   (default true)
 */
export default function SearchableDropdown({
  multi = false,
  value,
  values = [],
  onChange,
  options = [],
  placeholder = "Any",
  searchPlaceholder = "Search...",
  testId,
  disabled,
  clearable = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) setQuery("");
    else setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const selected = multi ? values : (value ? [value] : []);
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const handleToggle = (v) => {
    if (multi) {
      if (values.includes(v)) onChange(values.filter((x) => x !== v));
      else onChange([...values, v]);
    } else {
      onChange(v);
      setOpen(false);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(multi ? [] : "");
  };

  const displayLabel = () => {
    if (multi) {
      if (values.length === 0) return placeholder;
      if (values.length === 1) return options.find((o) => o.value === values[0])?.label || values[0];
      return `${values.length} selected`;
    }
    if (!value) return placeholder;
    return options.find((o) => o.value === value)?.label || value;
  };

  const hasValue = multi ? values.length > 0 : !!value;

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          className="w-full flex items-center justify-between bg-white border border-zinc-200 hover:border-zinc-950 disabled:opacity-50 disabled:pointer-events-none rounded-md shadow-sm h-9 text-sm px-3 text-left"
        >
          <span className={hasValue ? "text-zinc-950 truncate" : "text-zinc-400 truncate"}>
            {displayLabel()}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {hasValue && clearable && (
              <X
                className="h-3.5 w-3.5 text-zinc-400 hover:text-zinc-950"
                onClick={clear}
                data-testid={testId ? `${testId}-clear` : undefined}
              />
            )}
            <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[220px] p-0 bg-white border border-zinc-200" align="start">
        <div className="border-b border-zinc-100 p-2">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full h-8 px-2 text-sm outline-none border border-zinc-200 rounded focus:border-zinc-950"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-64 overflow-auto py-1">
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-zinc-400">No options</p>
          )}
          {filtered.map((o) => {
            const active = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => handleToggle(o.value)}
                data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-zinc-50 ${
                  active ? "text-zinc-950 font-semibold bg-zinc-50" : "text-zinc-700"
                }`}
              >
                <span className="truncate">{o.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-zinc-950 flex-shrink-0 ml-2" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
